import { z } from 'zod';

import {
  entityIdSchema,
  isoDateSchema,
  isoDateTimeSchema,
  moneySchema,
  pageMetaSchema,
} from './common.js';
import {
  canonicalizeFinancialCodeV1,
  canonicalizeFinancialSearchTextV1,
} from './financial-text-v1.js';
import { promoBenefitSchema, type PromoBenefit } from './promo-benefits.js';

const positiveRevisionSchema = z.number().int().positive();
const positiveMoneySchema = z.number().int().positive();
const idempotencyKeySchema = z.string().trim().min(8).max(200);
const financialSearchTextSchema = z
  .string()
  .transform(canonicalizeFinancialSearchTextV1)
  .pipe(z.string().max(200));
const financialAuditDetailValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const financialAuditEventSchema = z
  .object({
    id: entityIdSchema,
    action: z.string().min(1).max(160),
    actorId: entityIdSchema.nullable(),
    actorLabel: z.string().min(1).max(160),
    occurredAt: isoDateTimeSchema,
    details: z.record(z.string(), financialAuditDetailValueSchema),
  })
  .strict();
export const creditStatusSchema = z.enum(['active', 'used', 'expired', 'archived']);
export const creditListSortSchema = z.enum(['created_desc', 'expires_asc', 'balance_desc']);
export const creditOriginSchema = z.enum(['manual', 'reservation']);
export const creditListQuerySchema = z.object({
  cursor: z.string().trim().min(1).max(1_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: financialSearchTextSchema.optional(),
  status: creditStatusSchema.optional(),
  customerId: entityIdSchema.optional(),
  origin: creditOriginSchema.optional(),
  expiresFrom: isoDateSchema.optional(),
  expiresTo: isoDateSchema.optional(),
  sort: creditListSortSchema.default('created_desc'),
});
export const creditListCursorSchema = z.object({
  sort: creditListSortSchema,
  value: z.union([z.string(), z.number().int()]),
  id: entityIdSchema,
});

export const creditApplicationSchema = z.object({
  id: entityIdSchema,
  reservationId: entityIdSchema,
  reservationReference: z.string().min(1),
  amountCents: positiveMoneySchema,
  appliedAt: isoDateTimeSchema,
  appliedBy: entityIdSchema.nullable(),
});

export const creditSchema = z
  .object({
    id: entityIdSchema,
    customerId: entityIdSchema,
    customerName: z.string().min(1),
    customerArchived: z.boolean().default(false),
    sourceReservationId: entityIdSchema.nullable(),
    sourceReservationReference: z.string(),
    initialCents: positiveMoneySchema,
    usedCents: moneySchema,
    balanceCents: moneySchema,
    reason: z.string().min(1),
    expiresOn: isoDateSchema.nullable(),
    status: creditStatusSchema,
    revision: positiveRevisionSchema,
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export const creditDetailSchema = creditSchema.extend({
  applications: z.array(creditApplicationSchema),
  history: z.array(financialAuditEventSchema).default([]),
});
export const creditPageSchema = z.object({ items: z.array(creditSchema), page: pageMetaSchema });

export const creditCreateInputSchema = z
  .object({
    customerId: entityIdSchema,
    sourceReservationId: entityIdSchema.nullable().default(null),
    initialCents: positiveMoneySchema,
    reason: z.string().trim().min(1).max(2_000),
    expiresOn: isoDateSchema.nullable(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export const creditUpdateInputSchema = z
  .object({
    revision: positiveRevisionSchema,
    initialCents: positiveMoneySchema.optional(),
    reason: z.string().trim().min(1).max(2_000).optional(),
    expiresOn: isoDateSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.initialCents !== undefined ||
      value.reason !== undefined ||
      value.expiresOn !== undefined,
    { message: 'La modification ne contient aucun champ.' },
  );
export const creditApplicationInputSchema = z
  .object({
    reservationId: entityIdSchema,
    amountCents: positiveMoneySchema,
    revision: positiveRevisionSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export const financialArchiveInputSchema = z.object({ revision: positiveRevisionSchema }).strict();

export const reservationCancelWithCreditInputSchema = z
  .object({
    revision: positiveRevisionSchema,
    reason: z.string().trim().min(1).max(2_000),
    retainedCents: moneySchema,
    expiresOn: isoDateSchema.nullable(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export const reservationCancelWithCreditResultSchema = z.object({
  reservationId: entityIdSchema,
  reservationRevision: positiveRevisionSchema,
  creditId: entityIdSchema,
  creditInitialCents: positiveMoneySchema,
});

export const giftVoucherTypeSchema = z.enum(['amount', 'stay']);
export const giftVoucherStatusSchema = z.enum(['active', 'used', 'expired', 'archived']);
export const giftVoucherListSortSchema = z.enum(['created_desc', 'validity_asc', 'balance_desc']);
export const giftVoucherListQuerySchema = z.object({
  cursor: z.string().trim().min(1).max(1_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: financialSearchTextSchema.optional(),
  status: giftVoucherStatusSchema.optional(),
  type: giftVoucherTypeSchema.optional(),
  validFrom: isoDateSchema.optional(),
  validUntil: isoDateSchema.optional(),
  sort: giftVoucherListSortSchema.default('created_desc'),
});
export const giftVoucherExportQuerySchema = giftVoucherListQuerySchema.omit({
  cursor: true,
  limit: true,
});
export const giftVoucherListCursorSchema = z.object({
  sort: giftVoucherListSortSchema,
  value: z.union([z.string(), z.number().int()]),
  id: entityIdSchema,
});
export const giftVoucherUseSchema = z.object({
  id: entityIdSchema,
  reservationId: entityIdSchema,
  reservationReference: z.string().min(1),
  amountCents: positiveMoneySchema,
  usedAt: isoDateTimeSchema,
  usedBy: entityIdSchema.nullable(),
});
export const giftVoucherSchema = z
  .object({
    id: entityIdSchema,
    publicReference: z.string().trim().min(4).max(80),
    purchaserCustomerId: entityIdSchema.nullable(),
    purchaserLabel: z.string().min(1),
    beneficiaryCustomerId: entityIdSchema.nullable(),
    beneficiaryLabel: z.string().min(1),
    type: giftVoucherTypeSchema,
    stayId: entityIdSchema.nullable(),
    stayReference: z.string(),
    initialCents: positiveMoneySchema,
    usedCents: moneySchema,
    balanceCents: moneySchema,
    hasBeenUsed: z.boolean().default(false),
    validFrom: isoDateSchema,
    validUntil: isoDateSchema,
    message: z.string(),
    status: giftVoucherStatusSchema,
    revision: positiveRevisionSchema,
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export const giftVoucherDetailSchema = giftVoucherSchema.extend({
  uses: z.array(giftVoucherUseSchema),
  history: z.array(financialAuditEventSchema).default([]),
});
export const giftVoucherPageSchema = z.object({
  items: z.array(giftVoucherSchema),
  page: pageMetaSchema,
});

const giftVoucherCoreShape = {
  publicReference: z
    .string()
    .transform(canonicalizeFinancialCodeV1)
    .pipe(z.string().min(4).max(80))
    .optional(),
  purchaserCustomerId: entityIdSchema.nullable(),
  purchaserLabel: z.string().trim().min(1).max(240),
  beneficiaryCustomerId: entityIdSchema.nullable(),
  beneficiaryLabel: z.string().trim().min(1).max(240),
  type: giftVoucherTypeSchema,
  stayId: entityIdSchema.nullable(),
  initialCents: positiveMoneySchema,
  validFrom: isoDateSchema,
  validUntil: isoDateSchema,
  message: z.string().trim().max(2_000).default(''),
} as const;

function validateGiftVoucherInput(
  value: {
    readonly type?: 'amount' | 'stay' | undefined;
    readonly stayId?: string | null | undefined;
    readonly validFrom?: string | undefined;
    readonly validUntil?: string | undefined;
  },
  context: z.RefinementCtx,
): void {
  if (
    value.validFrom !== undefined &&
    value.validUntil !== undefined &&
    value.validUntil < value.validFrom
  ) {
    context.addIssue({ code: 'custom', path: ['validUntil'], message: 'Période invalide.' });
  }
  if (value.type === 'stay' && value.stayId === null) {
    context.addIssue({ code: 'custom', path: ['stayId'], message: 'Le Séjour est obligatoire.' });
  }
  if (value.type === 'amount' && value.stayId !== undefined && value.stayId !== null) {
    context.addIssue({
      code: 'custom',
      path: ['stayId'],
      message: 'Un Bon montant ne cible pas de Séjour.',
    });
  }
}

export const giftVoucherCreateInputSchema = z
  .object({ ...giftVoucherCoreShape, idempotencyKey: idempotencyKeySchema })
  .strict()
  .superRefine(validateGiftVoucherInput);
export const giftVoucherUpdateInputSchema = z
  .object({
    revision: positiveRevisionSchema,
    purchaserCustomerId: giftVoucherCoreShape.purchaserCustomerId.optional(),
    purchaserLabel: giftVoucherCoreShape.purchaserLabel.optional(),
    beneficiaryCustomerId: giftVoucherCoreShape.beneficiaryCustomerId.optional(),
    beneficiaryLabel: giftVoucherCoreShape.beneficiaryLabel.optional(),
    type: giftVoucherCoreShape.type.optional(),
    stayId: giftVoucherCoreShape.stayId.optional(),
    initialCents: giftVoucherCoreShape.initialCents.optional(),
    validFrom: giftVoucherCoreShape.validFrom.optional(),
    validUntil: giftVoucherCoreShape.validUntil.optional(),
    message: giftVoucherCoreShape.message.optional(),
  })
  .strict()
  .superRefine(validateGiftVoucherInput);
export const giftVoucherUseInputSchema = z
  .object({
    reservationId: entityIdSchema,
    amountCents: positiveMoneySchema,
    revision: positiveRevisionSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const promoCodeKindSchema = z.enum(['fixed', 'percentage', 'option']);
export const promoCodeScopeSchema = z.enum(['quote', 'reservation', 'both']);
export const promoCodeStatusSchema = z.enum([
  'active',
  'inactive',
  'outside_period',
  'quota_reached',
  'archived',
]);
export const promoCodeListSortSchema = z.enum(['updated_desc', 'code_asc', 'period_asc']);
export const promoCodeListQuerySchema = z.object({
  cursor: z.string().trim().min(1).max(1_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: financialSearchTextSchema.optional(),
  active: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((value) => value === true || value === 'true')
    .optional(),
  periodOn: isoDateSchema.optional(),
  type: promoCodeKindSchema.optional(),
  scope: promoCodeScopeSchema.optional(),
  sort: promoCodeListSortSchema.default('updated_desc'),
});
export const promoCodeListCursorSchema = z.object({
  sort: promoCodeListSortSchema,
  value: z.union([z.string(), z.number().int()]),
  id: entityIdSchema,
});
export const promoCodeUsageSchema = z.object({
  id: entityIdSchema,
  targetKind: z.enum(['quote', 'reservation']),
  targetId: entityIdSchema,
  customerId: entityIdSchema.nullable(),
  participantCount: z.number().int().nonnegative().nullable(),
  discountCents: moneySchema,
  appliedAt: isoDateTimeSchema,
  appliedBy: entityIdSchema.nullable(),
  releasedAt: isoDateTimeSchema.nullable(),
});
export const promoCodeSchema = z
  .object({
    id: entityIdSchema,
    code: z.string().trim().min(2).max(80),
    label: z.string().min(1),
    kind: promoCodeKindSchema,
    value: positiveMoneySchema,
    benefit: promoBenefitSchema.nullable().default(null),
    scope: promoCodeScopeSchema,
    validFrom: isoDateSchema.nullable(),
    validUntil: isoDateSchema.nullable(),
    quota: z.number().int().positive().nullable(),
    maxUsesPerCustomer: z.number().int().positive().nullable(),
    minimumParticipants: z.number().int().nonnegative().nullable(),
    usageCount: z.number().int().nonnegative(),
    active: z.boolean(),
    status: promoCodeStatusSchema,
    revision: positiveRevisionSchema,
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
  })
  .strict()
  .superRefine(validatePromoBenefit);
export const promoCodeDetailSchema = promoCodeSchema.extend({
  usages: z.array(promoCodeUsageSchema),
  history: z.array(financialAuditEventSchema).default([]),
});
export const promoCodePageSchema = z.object({
  items: z.array(promoCodeSchema),
  page: pageMetaSchema,
});

const promoCodeCoreShape = {
  code: z
    .string()
    .transform(canonicalizeFinancialCodeV1)
    .pipe(
      z
        .string()
        .min(2)
        .max(80)
        .regex(/^[\p{L}\p{N}][\p{L}\p{N}_-]*$/u),
    ),
  label: z.string().trim().min(1).max(240),
  kind: promoCodeKindSchema,
  value: positiveMoneySchema,
  benefit: promoBenefitSchema.nullable().default(null),
  scope: promoCodeScopeSchema,
  validFrom: isoDateSchema.nullable(),
  validUntil: isoDateSchema.nullable(),
  quota: z.number().int().positive().nullable(),
  maxUsesPerCustomer: z.number().int().positive().nullable().default(null),
  minimumParticipants: z.number().int().nonnegative().max(10_000).nullable().default(null),
  active: z.boolean(),
} as const;

function validatePromoCodeInput(
  value: {
    readonly kind?: 'fixed' | 'percentage' | 'option' | undefined;
    readonly value?: number | undefined;
    readonly benefit?: PromoBenefit | null | undefined;
    readonly validFrom?: string | null | undefined;
    readonly validUntil?: string | null | undefined;
  },
  context: z.RefinementCtx,
): void {
  validatePromoBenefit(value, context);
  if (value.kind === 'percentage' && value.value !== undefined && value.value > 10_000) {
    context.addIssue({ code: 'custom', path: ['value'], message: 'Le pourcentage dépasse 100 %.' });
  }
  if (
    value.validFrom !== undefined &&
    value.validUntil !== undefined &&
    value.validFrom !== null &&
    value.validUntil !== null &&
    value.validUntil < value.validFrom
  ) {
    context.addIssue({ code: 'custom', path: ['validUntil'], message: 'Période invalide.' });
  }
}

function validatePromoBenefit(
  value: {
    readonly kind?: 'fixed' | 'percentage' | 'option' | undefined;
    readonly value?: number | undefined;
    readonly benefit?: PromoBenefit | null | undefined;
  },
  context: z.RefinementCtx,
): void {
  if (value.kind === 'option' && value.benefit === null) {
    context.addIssue({
      code: 'custom',
      path: ['benefit'],
      message: 'Une option offerte exige une prestation structurée.',
    });
  }
  if (
    value.kind !== undefined &&
    value.kind !== 'option' &&
    value.benefit !== undefined &&
    value.benefit !== null
  ) {
    context.addIssue({
      code: 'custom',
      path: ['benefit'],
      message: 'Seul un Code promo option peut porter une prestation offerte.',
    });
  }
  if (
    value.kind === 'option' &&
    value.value !== undefined &&
    value.benefit !== undefined &&
    value.benefit !== null &&
    value.value !== value.benefit.estimatedUnitCents * (value.benefit.quantity ?? 1)
  ) {
    context.addIssue({
      code: 'custom',
      path: ['value'],
      message: 'La valeur du Code promo doit correspondre à la valeur estimée de la prestation.',
    });
  }
}

export const promoCodeCreateInputSchema = z
  .object({ ...promoCodeCoreShape, idempotencyKey: idempotencyKeySchema })
  .strict()
  .superRefine(validatePromoCodeInput);
export const promoCodeUpdateInputSchema = z
  .object({
    revision: positiveRevisionSchema,
    code: promoCodeCoreShape.code.optional(),
    label: promoCodeCoreShape.label.optional(),
    kind: promoCodeCoreShape.kind.optional(),
    value: promoCodeCoreShape.value.optional(),
    benefit: promoCodeCoreShape.benefit.optional(),
    scope: promoCodeCoreShape.scope.optional(),
    validFrom: promoCodeCoreShape.validFrom.optional(),
    validUntil: promoCodeCoreShape.validUntil.optional(),
    quota: promoCodeCoreShape.quota.optional(),
    maxUsesPerCustomer: promoCodeCoreShape.maxUsesPerCustomer.optional(),
    minimumParticipants: promoCodeCoreShape.minimumParticipants.optional(),
    active: promoCodeCoreShape.active.optional(),
  })
  .strict()
  .superRefine(validatePromoCodeInput);
export const promoTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('quote'), id: entityIdSchema }).strict(),
  z.object({ kind: z.literal('reservation'), id: entityIdSchema }).strict(),
]);
export const promoApplyInputSchema = z
  .object({
    target: promoTargetSchema,
    revision: positiveRevisionSchema,
    targetRevision: positiveRevisionSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export const promoReleaseInputSchema = promoApplyInputSchema;
export const promoMutationResultSchema = z.object({
  promoCodeId: entityIdSchema,
  usageId: entityIdSchema,
  target: promoTargetSchema,
  discountCents: moneySchema,
  targetRevision: positiveRevisionSchema,
  released: z.boolean(),
});

export type Credit = z.infer<typeof creditSchema>;
export type CreditApplication = z.infer<typeof creditApplicationSchema>;
export type CreditApplicationInput = z.infer<typeof creditApplicationInputSchema>;
export type CreditCreateInput = z.infer<typeof creditCreateInputSchema>;
export type CreditDetail = z.infer<typeof creditDetailSchema>;
export type CreditListCursor = z.infer<typeof creditListCursorSchema>;
export type CreditListQuery = z.output<typeof creditListQuerySchema>;
export type CreditUpdateInput = z.infer<typeof creditUpdateInputSchema>;
export type GiftVoucher = z.infer<typeof giftVoucherSchema>;
export type FinancialAuditEvent = z.infer<typeof financialAuditEventSchema>;
export type GiftVoucherCreateInput = z.infer<typeof giftVoucherCreateInputSchema>;
export type GiftVoucherDetail = z.infer<typeof giftVoucherDetailSchema>;
export type GiftVoucherListCursor = z.infer<typeof giftVoucherListCursorSchema>;
export type GiftVoucherListQuery = z.output<typeof giftVoucherListQuerySchema>;
export type GiftVoucherUpdateInput = z.infer<typeof giftVoucherUpdateInputSchema>;
export type GiftVoucherUseInput = z.infer<typeof giftVoucherUseInputSchema>;
export type PromoApplyInput = z.infer<typeof promoApplyInputSchema>;
export type PromoCode = z.infer<typeof promoCodeSchema>;
export type PromoCodeCreateInput = z.infer<typeof promoCodeCreateInputSchema>;
export type PromoCodeDetail = z.infer<typeof promoCodeDetailSchema>;
export type PromoCodeListCursor = z.infer<typeof promoCodeListCursorSchema>;
export type PromoCodeListQuery = z.output<typeof promoCodeListQuerySchema>;
export type PromoCodeUpdateInput = z.infer<typeof promoCodeUpdateInputSchema>;
export type PromoMutationResult = z.infer<typeof promoMutationResultSchema>;
export type ReservationCancelWithCreditInput = z.infer<
  typeof reservationCancelWithCreditInputSchema
>;
export type ReservationCancelWithCreditResult = z.infer<
  typeof reservationCancelWithCreditResultSchema
>;
